import { cn } from '@/lib/utils';

/**
 * The MirOnDo identity.
 *
 * Three brand elements, per the identity sheet:
 *
 *   D  — "udělej to"
 *   ON — "zapni se"      (the check ring)
 *   ✓  — "úkol splněn"   (the check inside it)
 *
 * The geometry lives here rather than in an imported `.svg` so it inherits
 * `currentColor` and scales with type. The same paths are generated into
 * `public/*.svg` for the favicon and app icons.
 */

/** The mark's own colour: forest green on light, sage on dark. */
const BRAND_COLOR = 'text-brand-ink dark:text-brand-400';

// Only the ring and the check remain here: the mark itself is now the brand
// logo, an image. These two still draw the notification glyph, which is 16px
// and has to inherit `currentColor`.
const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 14,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const RING = 'M116.5 15.4 A29 29 0 1 0 141 42.5';
const CHECK = 'M93 42 L109 57 L141.4 14.2';

/**
 * The mark.
 *
 * An image rather than the paths below it: this is the brand logo as the
 * designer drew it, rendered out of `Pics/mirondo-logo.png` by
 * `Scripts/generate-icons.swift`. The paths remain the source for the
 * notification glyph, which needs to inherit `currentColor` at 16px.
 *
 * Two files rather than one tinted image, because the mark is solid ink: on the
 * sidebar's forest green it has to be white, and on paper it keeps its own
 * colour. The pair is swapped by theme, or forced with `light`.
 */
export function LogoMark({
  className,
  light = false,
}: {
  className?: string;
  /** Always the white mark — for the sidebar, which is green in both themes. */
  light?: boolean;
}) {
  const size = cn('h-5 w-auto', className);
  if (light) {
    return <img src="/logo-mark-light.png" alt="MirOnDo" className={size} />;
  }
  return (
    <>
      <img src="/logo-mark.png" alt="MirOnDo" className={cn(size, 'dark:hidden')} />
      <img
        src="/logo-mark-light.png"
        alt=""
        aria-hidden
        className={cn(size, 'hidden dark:block')}
      />
    </>
  );
}

/**
 * The check ring alone.
 *
 * The identity sheet uses this on its own wherever the full wordmark would be
 * too wide or too fine — the notification glyph, and later the macOS menu bar.
 */
export function LogoCheck({ className }: { className?: string }) {
  return (
    <svg
      viewBox="75.8 7.2 72.8 72.8"
      className={cn('h-4 w-4', BRAND_COLOR, className)}
      role="img"
      aria-label="MirOnDo"
    >
      <g {...STROKE}>
        <path d={RING} />
        <path d={CHECK} />
      </g>
    </svg>
  );
}

/**
 * The whole logo — mark, name and tagline — as the designer drew it.
 *
 * For the one place it is the subject rather than a label: the sign-in screen.
 * Elsewhere `Logo` composes the mark with live text, which wraps and tints and
 * translates; this does none of that, and does not need to.
 */
export function LogoFull({ className }: { className?: string }) {
  const size = cn('h-auto w-full max-w-[15rem]', className);
  return (
    <>
      <img
        src="/logo-full.png"
        alt="MirOnDo — Turn on. Get it done."
        className={cn(size, 'dark:hidden')}
      />
      <img
        src="/logo-full-light.png"
        alt=""
        aria-hidden
        className={cn(size, 'hidden dark:block')}
      />
    </>
  );
}

/**
 * Mark plus name, and optionally the tagline.
 *
 * "MirOnDo" is set with its middle "On" in the accent colour, the way the
 * identity sheet does — the name contains the promise.
 */
export function Logo({
  tagline = false,
  className,
  markClassName,
  markLight = false,
  nameClassName,
  accentClassName,
}: {
  tagline?: boolean;
  className?: string;
  markClassName?: string;
  /** Force the white mark — the sidebar sits on the brand green. */
  markLight?: boolean;
  /** Override the wordmark colour — the sidebar sits on the brand green. */
  nameClassName?: string;
  /** Override the colour of "On" and "on." */
  accentClassName?: string;
}) {
  return (
    <span className={cn('inline-flex flex-col gap-1.5', className)}>
      <span className="inline-flex items-center gap-2.5">
        <LogoMark className={cn('h-6', markClassName)} light={markLight} />
        <span
          className={cn(
            'text-[15px] font-semibold tracking-tight text-content',
            nameClassName,
          )}
        >
          Mir<span className={cn('text-accent', accentClassName)}>On</span>Do
        </span>
      </span>
      {tagline ? (
        <span
          className={cn(
            'text-[10px] font-medium uppercase tracking-[0.14em] text-content-subtle',
            nameClassName,
          )}
        >
          Turn <span className={cn('text-accent', accentClassName)}>on.</span> Get it
          done.
        </span>
      ) : null}
    </span>
  );
}
